import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { MusicNotes, ArrowLeft, CalendarCheck, Info, Baby } from "@phosphor-icons/react";
import { BuyRelaxButton } from "@/components/BuyRelaxButton";

const DISCLAIMER = "Audio relaksasi bersifat edukasi dan pendampingan, bukan pengganti pemeriksaan atau penanganan tenaga kesehatan. Jangan didengarkan saat mengemudi atau melakukan aktivitas yang membutuhkan konsentrasi penuh.";
const rupiah = (n) => "Rp" + (n || 0).toLocaleString("id-ID");

export default function Relaksasi() {
  const { slug } = useParams();
  return slug ? <Detail slug={slug} /> : <Catalog />;
}

function Header() {
  return (
    <header className="border-b border-rose-100 bg-white/90 backdrop-blur">
      <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-3">
        <a href="/login" className="flex items-center gap-1.5 text-sm font-bold text-[#7C4DE0]"><ArrowLeft size={16} weight="bold" /> Beranda</a>
        <span className="flex items-center gap-1.5 font-heading text-base font-extrabold text-[#4C1D95]"><MusicNotes size={18} weight="duotone" className="text-[#DB2777]" /> Relaksasi</span>
      </div>
    </header>
  );
}

function Catalog() {
  const [data, setData] = useState({ products: [], bundles: [] });
  useEffect(() => {
    document.title = "Relaksasi — HalloGBS";
    axios.get(`${API}/public/relax`).then((r) => setData(r.data)).catch(() => {});
  }, []);
  return (
    <div className="min-h-screen bg-[#FAF7FF]" data-testid="relax-catalog">
      <Header />
      <main className="mx-auto max-w-3xl px-4 py-6">
        <h1 className="font-heading text-2xl font-extrabold text-[#4C1D95]">Audio Relaksasi HalloGBS</h1>
        <p className="mt-1 text-sm text-slate-500">Pendampingan hypnobirthing & hypno-breastfeeding. Dengarkan cuplikan gratis, beli untuk akses penuh seumur hidup.</p>
        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          {data.products.map((p) => (
            <Link key={p.id} to={`/relaksasi/${p.slug}`} data-testid={`relax-card-${p.slug}`} className="group overflow-hidden rounded-3xl border border-pink-100 bg-white transition-transform hover:-translate-y-0.5">
              <div className="aspect-[16/10] w-full bg-gradient-to-br from-[#EEE6FF] to-[#FDE7F1]">
                {p.has_cover && <img src={`${API}/public/relax/${p.id}/cover`} alt={p.title} className="h-full w-full object-cover" />}
              </div>
              <div className="p-4">
                <div className="text-[11px] font-bold uppercase tracking-wide text-[#DB2777]">{p.category_label || p.category}</div>
                <div className="mt-0.5 font-heading text-base font-extrabold text-[#1E1B4B]">{p.title}</div>
                {p.pregnancy_min_weeks ? <div className="mt-1 inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-bold text-amber-700"><Baby size={12} weight="fill" /> Untuk UK &gt;{p.pregnancy_min_weeks} minggu</div> : null}
                <div className="mt-2 font-heading text-sm font-extrabold text-[#7C4DE0]">{rupiah(p.price)}</div>
              </div>
            </Link>
          ))}
          {data.products.length === 0 && <p className="col-span-full rounded-2xl border border-dashed border-pink-200 py-10 text-center text-sm text-slate-400">Belum ada materi tersedia.</p>}
        </div>

        {data.bundles && data.bundles.length > 0 && (
          <section className="mt-8" data-testid="relax-bundles">
            <h2 className="font-heading text-lg font-extrabold text-[#4C1D95]">Paket Bundle Hemat</h2>
            <div className="mt-3 grid gap-4 sm:grid-cols-2">
              {data.bundles.map((b) => (
                <div key={b.id} data-testid={`relax-bundle-${b.id}`} className="rounded-3xl border border-[#7C4DE0]/20 bg-gradient-to-br from-[#F5F0FF] to-[#FDE7F1] p-5">
                  <div className="font-heading text-base font-extrabold text-[#1E1B4B]">{b.title}</div>
                  <div className="mt-1 text-xs text-slate-500">{(b.product_ids || []).length} materi</div>
                  <div className="mt-2 flex flex-wrap items-baseline gap-2">
                    {b.normal_price ? <span className="text-xs text-slate-400 line-through">{rupiah(b.normal_price)}</span> : null}
                    <span className="font-heading text-lg font-extrabold text-[#DB2777]">{rupiah(b.bundle_price)}</span>
                    {b.savings > 0 ? <span data-testid={`relax-bundle-savings-${b.id}`} className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-bold text-emerald-700">Hemat {rupiah(b.savings)}</span> : null}
                  </div>
                  <div className="mt-3"><BuyRelaxButton itemType="bundle" itemId={b.id} priceLabel={rupiah(b.bundle_price)} testid={`relax-buy-bundle-${b.id}`} /></div>
                </div>
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
}

function Detail({ slug }) {
  const [p, setP] = useState(undefined);
  useEffect(() => {
    axios.get(`${API}/public/relax/${slug}`)
      .then((r) => { setP(r.data); document.title = `${r.data.title} — Relaksasi HalloGBS`; })
      .catch(() => setP(null));
  }, [slug]);

  if (p === undefined) return <div className="flex min-h-screen items-center justify-center bg-[#FAF7FF] text-slate-400">Memuat…</div>;
  if (p === null) return (
    <div className="min-h-screen bg-[#FAF7FF]"><Header /><main className="mx-auto max-w-3xl px-4 py-16 text-center"><p data-testid="relax-404" className="text-slate-500">Materi tidak ditemukan.</p><Link to="/relaksasi" className="mt-4 inline-block font-bold text-[#7C4DE0]">← Kembali ke Relaksasi</Link></main></div>
  );

  return (
    <div className="min-h-screen bg-[#FAF7FF]" data-testid="relax-detail">
      <Header />
      <main className="mx-auto max-w-3xl px-4 py-6">
        <div className="overflow-hidden rounded-3xl border border-pink-100 bg-white">
          <div className="aspect-[16/9] w-full bg-gradient-to-br from-[#EEE6FF] to-[#FDE7F1]">
            {p.has_cover && <img src={`${API}/public/relax/${p.id}/cover`} alt={p.title} className="h-full w-full object-cover" />}
          </div>
          <div className="p-5">
            <div className="text-[11px] font-bold uppercase tracking-wide text-[#DB2777]">{p.category_label || p.category}</div>
            <h1 className="mt-1 font-heading text-2xl font-extrabold text-[#1E1B4B]">{p.title}</h1>
            {p.pregnancy_min_weeks ? <div data-testid="relax-uk-badge" className="mt-2 inline-flex items-center gap-1 rounded-full bg-amber-100 px-3 py-1 text-xs font-bold text-amber-700"><Baby size={14} weight="fill" /> Untuk usia kehamilan di atas {p.pregnancy_min_weeks} minggu</div> : null}
            <div className="mt-3 flex items-center gap-4 text-sm text-slate-600">
              {p.duration_sec ? <span>Durasi ~{Math.round(p.duration_sec / 60)} menit</span> : null}
              <span className="font-heading text-lg font-extrabold text-[#7C4DE0]">{rupiah(p.price)}</span>
            </div>

            {p.description && <Section title="Deskripsi" text={p.description} />}
            {p.benefits && <Section title="Manfaat" text={p.benefits} />}
            {p.usage_instructions && <Section title="Petunjuk Penggunaan" text={p.usage_instructions} />}

            {p.has_preview && (
              <div className="mt-5 rounded-2xl bg-[#FAF7FF] p-4">
                <div className="mb-2 text-sm font-bold text-[#4C1D95]">Dengarkan cuplikan gratis</div>
                <audio data-testid="relax-preview-audio" controls preload="none" className="w-full" src={`${API}/public/relax/${p.id}/preview`} />
              </div>
            )}

            <p data-testid="relax-disclaimer" className="mt-4 flex gap-2 rounded-xl bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-800"><Info size={16} weight="fill" className="mt-0.5 shrink-0" /> {DISCLAIMER}</p>

            <button data-testid="relax-buy-soon" style={{ display: "none" }} />
            <div className="mt-5" data-testid="relax-buy-block">
              <BuyRelaxButton itemType="product" itemId={p.id} priceLabel={rupiah(p.price)} testid="relax-buy-now" />
              <p className="mt-2 text-center text-[11px] text-slate-400">Pembelian satu kali · akses seumur hidup · butuh akun pasien terverifikasi</p>
            </div>
            <Link to="/booking" data-testid="relax-reservation-link" className="mt-3 flex items-center justify-center gap-2 rounded-2xl border-2 border-[#7C4DE0] px-5 py-2.5 font-heading text-sm font-extrabold text-[#7C4DE0]"><CalendarCheck size={18} weight="duotone" /> Buat Reservasi GBS</Link>
          </div>
        </div>
      </main>
    </div>
  );
}

function Section({ title, text }) {
  return (
    <div className="mt-4">
      <div className="font-heading text-sm font-extrabold text-[#4C1D95]">{title}</div>
      <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-slate-700">{text}</p>
    </div>
  );
}
